import React, { useState } from "react";
import "./ContactUsCenter.css";
import CustomerCenter from "./customer-center/CustomerCenter";
import InfoBoard from "./info-board/InfoBoard";
import BoardPostDetail from "./BoardPostDetail";
import { BOARD_GROUPS } from "../../services/contactCenterService";
import { useTranslation } from "react-i18next";

// English fallbacks for this component's common_ns keys. The backend common_ns source
// (Google Sheet -> ContentTranslation) overrides these once the keys are seeded; ko/vi
// are served from there. Keeps the component self-sufficient without a local bundle.
const DEFAULTS = {
  "CONTACT_US_CENTER.MAIN.CUSTOMER_CENTER": "Customer Center",
  "CONTACT_US_CENTER.MAIN.INFO_BOARD": "Info Board",
};

const MAIN_TABS = {
  CUSTOMER_CENTER: "CUSTOMER_CENTER",
  INFO_BOARD: "INFO_BOARD",
};

const TAB_QUERY_PARAM = "tab";
const POST_QUERY_PARAM = "postId";

const getTabFromUrl = () => {
  if (typeof window === "undefined") return MAIN_TABS.CUSTOMER_CENTER;
  const value = new URLSearchParams(window.location.search).get(
    TAB_QUERY_PARAM
  );
  return value && MAIN_TABS[value] ? value : MAIN_TABS.CUSTOMER_CENTER;
};

const getPostIdFromUrl = () => {
  if (typeof window === "undefined") return null;
  return new URLSearchParams(window.location.search).get(POST_QUERY_PARAM);
};

/** Rewrite the query string, preserving the hash and existing history state. */
const replaceQuery = (params) => {
  if (typeof window === "undefined") return;
  const query = params.toString();
  window.history.replaceState(
    window.history.state,
    "",
    `${window.location.pathname}${query ? `?${query}` : ""}${window.location.hash}`
  );
};

/**
 * Root of the /contact-us page.
 * Two main tabs: 고객센터 (Customer Center) / 정보 게시판 (Information Board).
 *
 * Opening a post (notice / info-board article) switches the page to a standalone detail view
 * addressed by ?postId=<publicId>; the board group is taken from the active main tab.
 */
const ContactUsCenter = () => {
  const [activeTab, setActiveTab] = useState(getTabFromUrl);
  const [activePostId, setActivePostId] = useState(getPostIdFromUrl);
  const { t: translate } = useTranslation("common_ns", { useSuspense: false });
  const t = (key, options) =>
    translate(key, { defaultValue: DEFAULTS[key] ?? key, ...options });

  const selectTab = (tab) => {
    setActiveTab(tab);
    const params = new URLSearchParams(window.location.search);
    params.set(TAB_QUERY_PARAM, tab);
    replaceQuery(params);
  };

  const openPost = (publicId) => {
    if (!publicId) return;
    setActivePostId(publicId);
    const params = new URLSearchParams(window.location.search);
    params.set(POST_QUERY_PARAM, publicId);
    replaceQuery(params);
    window.scrollTo(0, 0);
  };

  const closePost = () => {
    setActivePostId(null);
    const params = new URLSearchParams(window.location.search);
    params.delete(POST_QUERY_PARAM);
    replaceQuery(params);
  };

  // Standalone detail page — the board group comes from the active main tab.
  if (activePostId) {
    return (
      <div className="cuc-root">
        <BoardPostDetail
          group={BOARD_GROUPS[activeTab]}
          publicId={activePostId}
          onBack={closePost}
          onOpenPost={openPost}
        />
      </div>
    );
  }

  return (
    <div className="cuc-root">
      <div className="cuc-main-tabs">
        <button
          className={`cuc-main-tab ${
            activeTab === MAIN_TABS.CUSTOMER_CENTER ? "is-active" : ""
          }`}
          onClick={() => selectTab(MAIN_TABS.CUSTOMER_CENTER)}
        >
          <span role="img" aria-label="customer-center">📞</span>{" "}
          {t("CONTACT_US_CENTER.MAIN.CUSTOMER_CENTER")}
        </button>
        <button
          className={`cuc-main-tab ${
            activeTab === MAIN_TABS.INFO_BOARD ? "is-active" : ""
          }`}
          onClick={() => selectTab(MAIN_TABS.INFO_BOARD)}
        >
          <span role="img" aria-label="info-board">📄</span>{" "}
          {t("CONTACT_US_CENTER.MAIN.INFO_BOARD")}
        </button>
      </div>

      {activeTab === MAIN_TABS.CUSTOMER_CENTER ? (
        <CustomerCenter onOpenPost={openPost} />
      ) : (
        <InfoBoard onOpenPost={openPost} />
      )}
    </div>
  );
};

export default ContactUsCenter;
